import { CorrespondenceCompanyDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CorrespondenceCompanyActions from './company.actions';

export const CORRESPONDENCE_COMPANY_FEATURE_KEY = 'correspondenceCompany';

export interface CorrespondenceCompanyState
  extends EntityState<CorrespondenceCompanyDto> {
  selectedCorrespondenceCompanyId?: string | number; // which CorrespondenceCompany record has been selected
  loaded: boolean; // has the CorrespondenceCompany list been loaded
  error?: string | null; // last none error (if any)
}

export interface CorrespondenceCompanyPartialState {
  readonly [CORRESPONDENCE_COMPANY_FEATURE_KEY]: CorrespondenceCompanyState;
}

export const correspondenceCompanyAdapter: EntityAdapter<CorrespondenceCompanyDto> = createEntityAdapter<
  CorrespondenceCompanyDto
>({
  selectId: (correspondenceCompany) => correspondenceCompany.id,
  sortComparer: false,
});

export const initialState: CorrespondenceCompanyState = correspondenceCompanyAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedCorrespondenceCompanyId: null,
  }
);

const correspondenceCompanyReducer = createReducer(
  initialState,
  on(CorrespondenceCompanyActions.loadCorrespondenceCompany, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceCompanyActions.loadCorrespondenceCompanySuccess,
    (state, { correspondenceCompany }) =>
      correspondenceCompanyAdapter.addAll(correspondenceCompany, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceCompanyActions.loadCorrespondenceCompanyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceCompanyActions.addCorrespondenceCompany, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceCompanyActions.addCorrespondenceCompanySuccess,
    (state, { newCorrespondenceCompany }) =>
      correspondenceCompanyAdapter.addMany(newCorrespondenceCompany, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceCompanyActions.addCorrespondenceCompanyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceCompanyActions.updateCorrespondenceCompany, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceCompanyActions.updateCorrespondenceCompanySuccess,
    (state, { updateCorrespondenceCompany }) =>
      correspondenceCompanyAdapter.upsertMany(updateCorrespondenceCompany, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceCompanyActions.updateCorrespondenceCompanyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceCompanyActions.removeCorrespondenceCompany, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceCompanyActions.removeCorrespondenceCompanySuccess,
    (state, { ids }) =>
      correspondenceCompanyAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    CorrespondenceCompanyActions.removeCorrespondenceCompanyFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: CorrespondenceCompanyState | undefined,
  action: Action
) {
  return correspondenceCompanyReducer(state, action);
}
